import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';

import { FormattedMessage } from '../../../util/reactIntl';
import { IconSpinner } from '../../../components';

import SearchListingCard from './SearchListingCard';

import css from './SearchResultsPanel.module.css';

const DEV_MODE = process.env.NODE_ENV === 'development';
const noop = () => {};

const normalizeListingForCard = listing => {
  if (!listing || !listing.id || !listing.id.uuid) {
    return null;
  }

  const attributes = listing.attributes || {};
  const publicData = attributes.publicData || {};
  const images = Array.isArray(listing.images) ? listing.images.filter(Boolean) : [];

  return {
    ...listing,
    attributes: {
      ...attributes,
      publicData,
    },
    images,
  };
};

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {{ prev: ?string, next: ?string }} [props.crawlPageLinks] - Links to the previous and next result pages, for search engines
 * @param {Array<Object>} props.listings - The listings
 * @param {Function} [props.onLoadMore] - Callback for loading next page
 * @param {boolean} [props.hasNextPage] - Whether there is a next page
 * @param {boolean} [props.isFetchingNextPage] - Whether next page fetch is in progress
 * @param {boolean} [props.searchInProgress] - Whether initial search is in progress
 * @param {string} [props.queryKey] - Stable key for reset behavior
 * @param {Function} [props.onListingClick] - Callback fired before navigating to listing page
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    setActiveListing,
    isMapVariant = true,
    onLoadMore,
    hasNextPage = false,
    isFetchingNextPage = false,
    searchInProgress = false,
    queryKey,
    onListingClick,
    crawlPageLinks,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const warnedMissingFieldsRef = useRef(new Set());
  const warnedMissingIdsRef = useRef(false);

  const normalizedListings = useMemo(() => {
    const seen = new Set();
    const normalized = [];
    let hasMissingId = false;

    listings.forEach(rawListing => {
      const listingId = rawListing?.id?.uuid;
      if (!listingId) {
        hasMissingId = true;
        return;
      }
      if (seen.has(listingId)) {
        return;
      }

      const listing = normalizeListingForCard(rawListing);
      if (!listing) {
        hasMissingId = true;
        return;
      }

      seen.add(listingId);
      normalized.push(listing);
    });

    return { normalized, hasMissingId };
  }, [listings]);
  const loadedItemsCount = normalizedListings.normalized.length;
  const canLoadMore = Boolean(
    onLoadMore && hasNextPage && !isFetchingNextPage && !searchInProgress
  );
  const showEndMessage =
    !searchInProgress && !isFetchingNextPage && !hasNextPage && loadedItemsCount > 0;
  const infiniteScrollKey = queryKey || 'search-results';

  useEffect(() => {
    if (!DEV_MODE) {
      return;
    }

    if (normalizedListings.hasMissingId && !warnedMissingIdsRef.current) {
      // eslint-disable-next-line no-console
      console.warn('[SearchResultsPanel] Some listings are missing id.uuid and were skipped.');
      warnedMissingIdsRef.current = true;
    }

    normalizedListings.normalized.forEach(listing => {
      const listingId = listing?.id?.uuid;
      if (!listingId || warnedMissingFieldsRef.current.has(listingId)) {
        return;
      }

      const missingFields = [];
      if (!listing?.attributes?.title) {
        missingFields.push('attributes.title');
      }
      if (!listing?.attributes?.publicData) {
        missingFields.push('attributes.publicData');
      }
      if (!Array.isArray(listing?.images)) {
        missingFields.push('images');
      }

      if (missingFields.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[SearchResultsPanel] Listing missing card fields', {
          listingId,
          missingFields,
          queryKey,
        });
      }

      warnedMissingFieldsRef.current.add(listingId);
    });
  }, [normalizedListings, queryKey]);

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <InfiniteScroll
        key={infiniteScrollKey}
        className={css.infiniteScroll}
        dataLength={loadedItemsCount}
        next={canLoadMore ? onLoadMore : noop}
        hasMore={canLoadMore}
        hasChildren
        loader={null}
        endMessage={
          showEndMessage ? (
            <div className={css.endOfResults}>
              <span className={css.endOfResultsText}>
                <FormattedMessage id="SearchPage.endOfResults" />
              </span>
            </div>
          ) : null
        }
        scrollThreshold={0.7}
        style={{ overflow: 'visible' }}
      >
        <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
          {normalizedListings.normalized.map(l => (
            <li key={l.id.uuid} className={css.resultItem}>
              <SearchListingCard
                className={css.listingCard}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
                onListingClick={onListingClick}
              />
            </li>
          ))}

          {props.children}
        </ul>
      </InfiniteScroll>

      {crawlPageLinks?.prev || crawlPageLinks?.next ? (
        <nav className={css.crawlPageLinks} aria-label="Result pages">
          {crawlPageLinks.prev ? (
            <a href={crawlPageLinks.prev} rel="prev">
              <FormattedMessage id="SearchPage.previousPage" />
            </a>
          ) : null}
          {crawlPageLinks.next ? (
            <a href={crawlPageLinks.next} rel="next">
              <FormattedMessage id="SearchPage.nextPage" />
            </a>
          ) : null}
        </nav>
      ) : null}

      {isFetchingNextPage ? (
        <div className={css.loadingMore}>
          <IconSpinner />
          <span className={css.loadingMoreText}>
            <FormattedMessage id="SearchPage.loadingMore" />
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default SearchResultsPanel;
